// frontend/src/pages/Company/Auth/Signup.jsx
import React, { useEffect, useState, useCallback } from "react";
import { authActions } from "../../../../redux-store";
import { useDispatch, useSelector } from "react-redux";
import backend from "../../../../api";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, easeOut, AnimatePresence } from "framer-motion";
import Classes from "./Auth.module.css";
import CryptoJS from "crypto-js";
import { EyeCloseIcon, EyeOpenIcon } from "../../../../utils/Icons";

const LoadingSpinner = () => (
  <div className="loadingio-spinner-eclipse-nq4q5u6dq7r">
    <div className="ldio-x2uulkbinbj">
      <div></div>
    </div>
  </div>
);

const AdminHashCode = "x4a679ffdfdfqAEF5";
const ManagerHashCode = "managerHash123";
const ClientHashCode = "clientHash456";
const existingUserHashCode = "asasaderuxrtdfqE31xB"; // activates existing user by invite

function Signup() {
  const [state, setState] = useState("signup");

  // NEW: first / last / phone (instead of full name)
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [phone,     setPhone]     = useState("");

  // existing
  const [email, setEmail]                 = useState("");
  const [password, setPassword]           = useState("");
  const [confirmPassword, setconfirmPassword] = useState("");
  const [loading, setLoading]             = useState(false);
  const [incorrectError, setincorrectError] = useState(false);
  const [showEye, setshowEye]             = useState(false);
  const [showEye2, setshowEye2]           = useState(false);
  const [passwordMatchingError, setpasswordMatchingError] = useState(false);
  const [passwordLengthError, setpasswordLengthError]     = useState(false);
  const [userAlreadyExistError, setUserAlreadyExistError] = useState(false);
  const [errorMessage, setErrorMessage]   = useState("");
  const [codeSentMsg, setcodeSentMsg]     = useState(false);
  const [IncorrectCode, setIncorrectCode] = useState(false);
  const [userCode, setUserCode]           = useState("");

  const dispatch  = useDispatch();
  const navigateTo = useNavigate();
  const location  = useLocation();

  // Invite params (integrated from AuthInvitee)
  const { hashcode, senderId, inviteeId, parentcompanyId } = useParams();

  // If you keep company info in redux (for colors/logos), grab it if needed
  const onBoarding = useSelector((s) => s.companyInfo?.info);
  const companyId  = onBoarding?._id || parentcompanyId; // prefer parentcompanyId when coming via invite

  // email verification
  const code    = Math.floor(Math.random() * 10000).toString();
  const message = `verification code is ${code}`;

  // ─────────────────────────────
  // Invite validation (from AuthInvitee)
  // ─────────────────────────────
  const getInviteStatus = useCallback(async () => {
    if (!inviteeId) return; // not an invite flow
    setLoading(true);
    try {
      const resp = await backend.get(`/api/invite/${inviteeId}`);
      if (resp?.data?.isAccepted || !resp?.data?.status) {
        navigateTo("/invitationexpired");
        return;
      }
    } catch (e) {
      navigateTo("/invitationexpired");
      console.error(e);
      return;
    } finally {
      setLoading(false);
    }

    // If invite is for an EXISTING user, auto-activate them
    if (existingUserHashCode === hashcode && senderId) {
      try {
        setLoading(true);
        const [inviteResponse, userResponse] = await Promise.all([
          backend.put(`/api/invite/${inviteeId}`, { isAccepted: true }),
          backend.put(`/api/user/${senderId}`,  { status: "active" }),
        ]);
        if (inviteResponse.status === 200 && userResponse.status === 200) {
          alert("Congratulations! Your account is active now");
          navigateTo("/dashboard");
        }
      } catch (e) {
        console.error("Failed existing-user activation", e);
      } finally {
        setLoading(false);
      }
    }
  }, [inviteeId, hashcode, senderId, navigateTo]);

  useEffect(() => { getInviteStatus(); }, [getInviteStatus]);

  // ─────────────────────────────
  // Verify & Signup flow
  // ─────────────────────────────
  const switchToVerify = () => {
    setincorrectError(false);

    if (password !== confirmPassword) {
      setpasswordMatchingError(true);
      setTimeout(() => setpasswordMatchingError(false), 5000);
      return;
    }

    if (password && password.length <= 5) {
      setpasswordLengthError(true);
      setTimeout(() => setpasswordLengthError(false), 10000);
      return;
    }

    if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
      setincorrectError(true);
      setErrorMessage("Please fill first name, last name and phone.");
      setTimeout(() => { setincorrectError(false); setErrorMessage(""); }, 5000);
      return;
    }

    sendMail();
  };

  const sendMail = async () => {
    setLoading(true);
    try {
      const encrypted = CryptoJS.AES.encrypt(code, "secret-key").toString();
      sessionStorage.setItem("encryptedData", encrypted);

      const body = { username: "User", email, message, phone };
      const resp = await backend.post("/verify/sendemail", body);

      if (resp.status === 200) {
        setcodeSentMsg(true);
        setTimeout(() => setcodeSentMsg(false), 60000);
        setState("verify");
      } else if (resp.status === 400) {
        setUserAlreadyExistError(true);
        setTimeout(() => setUserAlreadyExistError(false), 10000);
      } else if (resp.status === 500) {
        alert("Server error occurred. Please try again later.");
      } else {
        alert("An unexpected error occurred. Please try again.");
      }
    } catch (error) {
      setUserAlreadyExistError(true);
      setErrorMessage(error?.response?.data?.message || "Error sending email");
      setTimeout(() => {
        setUserAlreadyExistError(false);
        setErrorMessage("");
      }, 10000);
    } finally {
      setLoading(false);
    }
  };

  const resolveRole = () => {
    if (hashcode === AdminHashCode)   return "company";
    if (hashcode === ManagerHashCode) return "employee";
    if (hashcode === ClientHashCode)  return "customer";
    return "company"; // default for standard company signup page
  };

  const SignUpHandler = async () => {
    setLoading(true);
    const role = resolveRole();
    const name = `${firstName} ${lastName}`.trim();
    const companySubAdmin = true;
    const isInvitee = !!inviteeId;

    try {
      if (email && password && email.includes("@") && password.length > 5) {
        const isSignup = await dispatch(
          authActions.InviteSignUp(
            email,
            password,
            role,
            name,
            phone,
            companyId,          // pass company for invites/tenancy
            companySubAdmin,    // sub-admin if company role
            isInvitee,          // invite flag
            inviteeId           // invite id so backend can mark accepted & link user
          )
        );

        if (isSignup) {
          setLoading(false);
          // Company signup goes to onboarding; invitees can go to dashboard (adjust if needed)
          if (role === "company" && !isInvitee) {
            navigateTo("/on-boarding");
          } else {
            navigateTo("/dashboard");
          }
        } else {
          setLoading(false);
          setincorrectError(true);
        }
      } else {
        setLoading(false);
        setpasswordLengthError(true);
        setTimeout(() => setpasswordLengthError(false), 10000);
      }
    } catch (e) {
      setLoading(false);
      alert("Network Error");
    }
  };

  const handleSignUpSubmit = (e) => {
    e.preventDefault();
    if (email?.startsWith("demo.evala")) {
      // bypass email verification for demo users
      SignUpHandler();
    } else {
      switchToVerify();
    }
  };

  const onCLickVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    const encryptedDataFromStorage = sessionStorage.getItem("encryptedData");
    if (encryptedDataFromStorage) {
      const bytes = CryptoJS.AES.decrypt(encryptedDataFromStorage, "secret-key");
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      if (userCode === decryptedData) {
        await SignUpHandler();
      } else {
        setIncorrectCode(true);
        setLoading(false);
        setTimeout(() => setIncorrectCode(false), 1000);
      }
    } else {
      setLoading(false);
      setIncorrectCode(true);
      setTimeout(() => setIncorrectCode(false), 1000);
    }
  };

  // Single animation for the whole form (slide down + fade in)
  const wholeFormInitial = { y: -16, opacity: 0 };
  const wholeFormAnimate = {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: easeOut, delay: 0.15 },
  };

  return (
    <>
      <div className={Classes.navContainer}>
        <div></div>
        <a href="/" className={Classes.miniLogo2} style={{ textDecoration: "none" }}>
          <div className="logo">
            <img src="/assets/images/logo.webp" alt="logo" />
          </div>
        </a>
        <div></div>
      </div>

      <div className={Classes.signUp}>
        <AnimatePresence mode="wait">
          {state === "signup" && (
            <motion.div
              key="signup"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 0.1 }}
            >
              <motion.form
                onSubmit={handleSignUpSubmit}
                className={Classes.signUpInnerCon}
                initial={wholeFormInitial}
                animate={wholeFormAnimate}
              >
                <h5 className={Classes.heading2}>Welcome to Fuel Clubs</h5>
                <p className={Classes.subtitle}>
                  {inviteeId ? "You're joining via an invitation" : "Sign up today to continue to your dashboard"}
                </p>

                {/* First Name */}
                <input
                  onChange={(e) => setFirstName(e.target.value)}
                  type="text"
                  placeholder="First name"
                  required
                  className={`${Classes.inputField} ${Classes.mb20}`}
                />

                {/* Last Name */}
                <input
                  onChange={(e) => setLastName(e.target.value)}
                  type="text"
                  placeholder="Last name"
                  required
                  className={`${Classes.inputField} ${Classes.mb20}`}
                />

                {/* Email */}
                <input
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="Enter your email"
                  required
                  className={`${Classes.inputField} ${Classes.mb20} ${incorrectError || userAlreadyExistError ? Classes.inputError : ""}`}
                />

                {/* Phone */}
                <input
                  onChange={(e) => setPhone(e.target.value)}
                  type="tel"
                  placeholder="Enter your phone"
                  required
                  className={`${Classes.inputField} ${Classes.mb20}`}
                />

                {/* Password */}
                <div
                  className={`${Classes.passwordCon} ${Classes.mb20} ${
                    incorrectError || passwordMatchingError || passwordLengthError ? Classes.inputError : ""
                  }`}
                >
                  <input
                    onChange={(e) => setPassword(e.target.value)}
                    type={showEye ? "text" : "password"}
                    placeholder="Enter your password"
                    required
                  />
                  {showEye ? (
                    <EyeOpenIcon onClick={() => setshowEye(!showEye)} />
                  ) : (
                    <EyeCloseIcon onClick={() => setshowEye(!showEye)} />
                  )}
                </div>

                {/* Confirm Password */}
                <div
                  className={`${Classes.passwordCon} ${Classes.mb20} ${
                    incorrectError || passwordMatchingError || passwordLengthError ? Classes.inputError : ""
                  }`}
                >
                  <input
                    onChange={(e) => setconfirmPassword(e.target.value)}
                    type={showEye2 ? "text" : "password"}
                    placeholder="Confirm password"
                    required
                  />
                  {showEye2 ? (
                    <EyeOpenIcon onClick={() => setshowEye2(!showEye2)} />
                  ) : (
                    <EyeCloseIcon onClick={() => setshowEye2(!showEye2)} />
                  )}
                </div>

                {/* Policies */}
                <p className={Classes.signupLink} style={{ maxWidth: "100%" }}>
                  By creating an account, you agree to the{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                    <button type="button" className={Classes.linkButton}>Privacy Policy</button>
                  </a>
                  <span> and </span>
                  <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                    <button type="button" className={Classes.linkButton}>Terms of Service</button>
                  </a>
                </p>

                {/* Submit (no login button anywhere) */}
                <button type="submit" className={`${Classes.button} ${Classes.signInButton}`} disabled={loading}>
                  {inviteeId ? "Accept & create account" : "Create an account"}
                  {loading && <LoadingSpinner />}
                </button>

                {/* (Removed login button per request) */}
                {incorrectError && !!errorMessage && (
                  <p className={Classes.error} style={{ marginTop: 10 }}>{errorMessage}</p>
                )}
                {passwordMatchingError && <p className={Classes.error}>*Passwords are not matching.</p>}
                {passwordLengthError && <p className={Classes.error}>*Password length must be &gt; 5.</p>}
                {userAlreadyExistError && <p className={Classes.error}>{errorMessage || "*User already exists."}</p>}
              </motion.form>
            </motion.div>
          )}

          {state === "verify" && (
            <motion.div
              key="verify"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.5, ease: easeOut, delay: 0.1 }}
            >
              <motion.form
                onSubmit={onCLickVerify}
                className={Classes.signUpInnerCon}
                initial={wholeFormInitial}
                animate={wholeFormAnimate}
              >
                <h1 className={Classes.heading2}>Enter verification code</h1>
                {codeSentMsg && <p className={Classes.subtitle}>A code was sent to your email</p>}
                <br />
                <input
                  onChange={(e) => setUserCode(e.target.value)}
                  type="number"
                  value={userCode}
                  placeholder="Enter your code here"
                  required
                  className={`${Classes.inputField} ${Classes.mb10} ${incorrectError ? Classes.inputError : ""}`}
                />
                {IncorrectCode && <p className={Classes.error}>*Incorrect code.</p>}
                <button type="submit" className={`${Classes.button} ${Classes.signInButton}`} disabled={loading}>
                  Verify
                  {loading && <LoadingSpinner />}
                </button>
                <p className={Classes.signupLink2}>
                  Haven't received the code?
                  <button type="button" onClick={() => switchToVerify()} className={Classes.linkButton}>
                    Resend code
                  </button>
                </p>
              </motion.form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

export default Signup;
